import { addDays, formatPrice, todayIso, type PositionView } from '@stockstop/shared';
import { useEffect, useState } from 'react';
import { Modal } from '../../components/Modal.js';

/**
 * Dialog zum Einbuchen eines Stop-Loss.
 *
 * Eingebucht wird der Kurs, der im Moment der Bestätigung gilt — er wird deshalb
 * angezeigt, nicht eingegeben. Zu wählen ist allein, **wie lange** die Order beim
 * Broker liegt: Viele Broker befristen Stop-Orders, und nach Ablauf ist die
 * Position ungeschützt, ohne dass irgendetwas darauf hinweist.
 */

interface BookingDialogProps {
  position: PositionView | null;
  isPending: boolean;
  onConfirm: (validUntil: string) => void;
  onCancel: () => void;
}

/** Gängige Laufzeiten, damit der Kalender meist gar nicht nötig ist. */
const PRESETS = [
  { label: '1 Monat', days: 30 },
  { label: '3 Monate', days: 90 },
  { label: '1 Jahr', days: 365 },
] as const;

export const BookingDialog = ({
  position,
  isPending,
  onConfirm,
  onCancel,
}: BookingDialogProps): React.JSX.Element => {
  const [validUntil, setValidUntil] = useState<string>(() => addDays(todayIso(), 90));

  /* Bei jedem Öffnen auf den Standard zurück, statt die letzte Wahl zu erben. */
  useEffect(() => {
    if (position) setValidUntil(addDays(todayIso(), 90));
  }, [position]);

  const stopPrice = position?.stopLossEvaluation.stopPrice ?? null;
  const currency = position?.instrument.currency ?? null;
  const today = todayIso();
  const isValid = validUntil >= today;

  return (
    <Modal
      isOpen={position !== null}
      title={`Stop-Loss einbuchen${position ? ` · ${position.instrument.symbol}` : ''}`}
      onClose={onCancel}
      actions={
        <>
          <button type="button" onClick={onCancel} disabled={isPending}>
            Abbrechen
          </button>
          <button
            type="button"
            className="button-primary"
            onClick={() => onConfirm(validUntil)}
            disabled={isPending || !isValid || stopPrice === null}
          >
            {isPending ? 'Bucht ein …' : 'Einbuchen'}
          </button>
        </>
      }
    >
      <p className="booking-dialog__intro">
        Bestätige, dass du diese Stop-Loss-Order bei deinem Broker eingestellt hast.
      </p>

      <div className="booking-dialog__price">
        <span className="field__label">Eingebuchter Kurs</span>
        <strong>{stopPrice === null ? '—' : formatPrice(stopPrice, { currency })}</strong>
      </div>

      <label className="field">
        <span className="field__label">Gültig bis</span>
        <input
          type="date"
          value={validUntil}
          min={today}
          onChange={(event) => setValidUntil(event.target.value)}
        />
      </label>

      <div className="button-group booking-dialog__presets">
        {PRESETS.map((preset) => (
          <button
            key={preset.days}
            type="button"
            onClick={() => setValidUntil(addDays(today, preset.days))}
          >
            {preset.label}
          </button>
        ))}
      </div>

      {!isValid ? <p className="hint hint--error">Das Datum liegt in der Vergangenheit.</p> : null}
    </Modal>
  );
};
